// src/components/dashboard/RiskTrends.jsx
import React, { useState } from 'react';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid,
    Tooltip, Legend, ResponsiveContainer, BarChart, Bar
} from 'recharts';
import {
    Box, Typography, ToggleButtonGroup, ToggleButton,
    FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import {
    Timeline as TimelineIcon,
    BarChart as BarChartIcon
} from '@mui/icons-material';

const RiskTrends = ({ assessments }) => {
    const [chartType, setChartType] = useState('line');
    const [timeRange, setTimeRange] = useState('all');

    if (!assessments || assessments.length === 0) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
                <Typography variant="body1" color="text.secondary">
                    No assessment data available for trend analysis.
                </Typography>
            </Box>
        );
    }

    // Sort assessments by date
    const sortedAssessments = [...assessments].sort((a, b) =>
        new Date(a.created_at) - new Date(b.created_at)
    );

    // Filter by time range if needed
    let filteredAssessments = sortedAssessments;
    if (timeRange !== 'all') {
        const now = new Date();
        const cutoffDate = new Date();

        if (timeRange === '30days') {
            cutoffDate.setDate(now.getDate() - 30);
        } else if (timeRange === '90days') {
            cutoffDate.setDate(now.getDate() - 90);
        } else if (timeRange === '12months') {
            cutoffDate.setMonth(now.getMonth() - 12);
        }

        filteredAssessments = sortedAssessments.filter(assessment =>
            new Date(assessment.created_at) >= cutoffDate
        );
    }

    // Prepare data for charts
    const chartData = filteredAssessments.map(assessment => ({
        date: new Date(assessment.created_at).toLocaleDateString(),
        score: assessment.risk_score,
        domain: assessment.domain.charAt(0).toUpperCase() + assessment.domain.slice(1)
    }));

    // Group by domain for domain-specific analysis
    const domainData = {};
    filteredAssessments.forEach(assessment => {
        const domain = assessment.domain;
        if (!domainData[domain]) {
            domainData[domain] = [];
        }
        domainData[domain].push(assessment);
    });

    // Get unique domains
    const domains = Object.keys(domainData);

    // Average risk scores by domain
    const domainAverages = domains.map(domain => {
        const items = domainData[domain];
        const total = items.reduce((sum, item) => sum + item.risk_score, 0);
        return {
            domain: domain.charAt(0).toUpperCase() + domain.slice(1),
            averageScore: total / items.length
        };
    });

    const handleChartTypeChange = (event, newType) => {
        if (newType !== null) {
            setChartType(newType);
        }
    };

    const handleTimeRangeChange = (event) => {
        setTimeRange(event.target.value);
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <ToggleButtonGroup
                    value={chartType}
                    exclusive
                    onChange={handleChartTypeChange}
                    size="small"
                >
                    <ToggleButton value="line">
                        <TimelineIcon fontSize="small" sx={{ mr: 0.5 }} />
                        Line
                    </ToggleButton>
                    <ToggleButton value="bar">
                        <BarChartIcon fontSize="small" sx={{ mr: 0.5 }} />
                        Bar
                    </ToggleButton>
                </ToggleButtonGroup>

                <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel id="time-range-label">Time Range</InputLabel>
                    <Select
                        labelId="time-range-label"
                        id="time-range"
                        value={timeRange}
                        label="Time Range"
                        onChange={handleTimeRangeChange}
                    >
                        <MenuItem value="all">All Time</MenuItem>
                        <MenuItem value="30days">Last 30 Days</MenuItem>
                        <MenuItem value="90days">Last 90 Days</MenuItem>
                        <MenuItem value="12months">Last 12 Months</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            <Box sx={{ height: 300, width: '100%' }}>
                {chartType === 'line' ? (
                    <ResponsiveContainer>
                        <LineChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" />
                            <YAxis domain={[0, 10]} />
                            <Tooltip />
                            <Legend />
                            <Line
                                type="monotone"
                                dataKey="score"
                                name="Risk Score"
                                stroke="#8884d8"
                                activeDot={{ r: 8 }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                ) : (
                    <ResponsiveContainer>
                        <BarChart data={domainAverages}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="domain" />
                            <YAxis domain={[0, 10]} />
                            <Tooltip />
                            <Legend />
                            <Bar
                                dataKey="averageScore"
                                name="Avg Risk Score"
                                fill="#8884d8"
                                label={{ position: 'top', formatter: (value) => value.toFixed(1) }}
                            />
                        </BarChart>
                    </ResponsiveContainer>
                )}
            </Box>

            <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                    {chartType === 'line'
                        ? 'Line chart shows risk score trends over time.'
                        : 'Bar chart shows average risk scores by domain.'}
                </Typography>
            </Box>
        </Box>
    );
};

export default RiskTrends;